import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, Plus, Trash2, Lock, Eye, EyeOff, Shield, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
}

export const AdminSettings = ({ admin }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // New admin form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  // Change password form
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin-auth/admins`, { headers: getAuthHeaders() });
      if (res.ok) setAdmins(await res.json());
    } catch (e) {
      console.error("Failed to fetch admins:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim() || !newPassword) {
      setAddError("All fields are required"); return;
    }
    if (newPassword.length < 8) {
      setAddError("Password must be at least 8 characters"); return;
    }
    setAddLoading(true);
    setAddError("");
    try {
      const res = await fetch(`${API}/admin-auth/admins`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ name: newName.trim(), email: newEmail.trim(), password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) { setAddError(data.detail || "Failed to create admin"); return; }
      setAdmins(prev => [...prev, data]);
      setShowAddForm(false);
      setNewName(""); setNewEmail(""); setNewPassword("");
    } catch {
      setAddError("Connection error");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteAdmin = async (id) => {
    try {
      const res = await fetch(`${API}/admin-auth/admins/${id}`, {
        method: "DELETE", headers: getAuthHeaders()
      });
      if (res.ok) {
        setAdmins(prev => prev.filter(a => a.id !== id));
      } else {
        const data = await res.json();
        alert(data.detail || "Failed to delete admin");
      }
    } catch {
      alert("Connection error");
    }
    setDeleteTarget(null);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError(""); setPwdSuccess("");
    if (!currentPwd || !newPwd || !confirmPwd) {
      setPwdError("All fields are required"); return;
    }
    if (newPwd.length < 8) {
      setPwdError("New password must be at least 8 characters"); return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError("Passwords do not match"); return;
    }
    setPwdLoading(true);
    try {
      const res = await fetch(`${API}/admin-auth/change-password`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ current_password: currentPwd, new_password: newPwd })
      });
      const data = await res.json();
      if (!res.ok) { setPwdError(data.detail || "Failed to change password"); return; }
      setPwdSuccess("Password changed successfully");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch {
      setPwdError("Connection error");
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-cabinet">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your team and account security</p>
      </div>

      {/* Team Members */}
      <Card className="border-slate-200" data-testid="team-members-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-cabinet">
              <Users size={20} className="text-royal-blue-600" /> Team Members
            </CardTitle>
            <Button
              size="sm"
              onClick={() => { setShowAddForm(!showAddForm); setAddError(""); }}
              className="bg-royal-blue-600 hover:bg-royal-blue-700"
              data-testid="add-admin-toggle-btn"
            >
              <Plus size={16} className="mr-1" /> Add Admin
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showAddForm && (
            <form onSubmit={handleAddAdmin} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="add-admin-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm text-slate-600">Name</Label>
                  <Input
                    data-testid="new-admin-name-input"
                    value={newName} onChange={e => setNewName(e.target.value)}
                    placeholder="Full name" className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Email</Label>
                  <Input
                    data-testid="new-admin-email-input"
                    type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                    placeholder="email@oplogman.com" className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Password</Label>
                  <Input
                    data-testid="new-admin-password-input"
                    type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters" className="mt-1"
                  />
                </div>
              </div>
              {addError && <p className="text-red-500 text-sm" data-testid="add-admin-error">{addError}</p>}
              <div className="flex gap-2">
                <Button type="submit" disabled={addLoading} size="sm" className="bg-royal-blue-600 hover:bg-royal-blue-700" data-testid="add-admin-submit-btn">
                  {addLoading ? "Creating..." : "Create Admin"}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="text-center py-6 text-slate-400">Loading team...</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {admins.map(a => (
                <div key={a.id} className="flex items-center justify-between py-3" data-testid={`admin-row-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-royal-blue-100 text-royal-blue-700 flex items-center justify-center font-bold text-sm">
                      {a.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {a.name}
                        {a.id === admin?.id && <span className="ml-2 text-xs text-royal-blue-600 bg-royal-blue-50 px-2 py-0.5 rounded-full">You</span>}
                      </p>
                      <p className="text-xs text-slate-400">{a.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {a.last_login && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock size={12} /> Last login: {new Date(a.last_login).toLocaleDateString()}
                      </span>
                    )}
                    <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Shield size={12} /> Admin
                    </span>
                    {a.id !== admin?.id && (
                      deleteTarget === a.id ? (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteAdmin(a.id)} data-testid={`confirm-delete-${a.id}`}>
                            Confirm
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteTarget(a.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          data-testid={`delete-admin-${a.id}`}
                          title="Remove admin"
                        >
                          <Trash2 size={15} />
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-slate-200" data-testid="change-password-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-cabinet">
            <Lock size={20} className="text-amber-600" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Current Password</Label>
              <div className="relative">
                <Input
                  data-testid="current-password-input"
                  type={showCurrentPwd ? "text" : "password"}
                  value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
                  className="pr-10"
                />
                <button type="button" onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrentPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">New Password</Label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showNewPwd ? "text" : "password"}
                  value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  placeholder="Min 8 characters" className="pr-10"
                />
                <button type="button" onClick={() => setShowNewPwd(!showNewPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Confirm New Password</Label>
              <Input
                data-testid="confirm-password-input"
                type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
              />
            </div>

            {pwdError && <p className="text-red-500 text-sm" data-testid="change-password-error">{pwdError}</p>}
            {pwdSuccess && <p className="text-emerald-600 text-sm" data-testid="change-password-success">{pwdSuccess}</p>}

            <Button type="submit" disabled={pwdLoading} className="bg-amber-600 hover:bg-amber-700" data-testid="change-password-btn">
              {pwdLoading ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
